import { useState } from "react";
import ChatBubbleLeftRightIcon from "@heroicons/react/24/outline/ChatBubbleLeftRightIcon";
import LinkIcon from "@heroicons/react/24/outline/LinkIcon";
import ChartBarIcon from "@heroicons/react/24/outline/ChartBarIcon";
import MagnifyingGlassPlusIcon from "@heroicons/react/24/outline/MagnifyingGlassPlusIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";

const secondaryFeatures = [
  {
    icon: LinkIcon,
    title: "Seamless Integration",
    description:
      "Connect Garmin or Strava in seconds. Your data syncs automatically, so your AI coach always has the full picture of your training.",
  },
  {
    icon: ChartBarIcon,
    title: "Training Visualization",
    description:
      "See your progress with clear charts and metrics. Track pace, heart rate, training load, and more. Your AI coach uses all of this to give you better answers.",
  },
];

export function FeaturesSection() {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  return (
    <section id="features" className="py-12 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Not just insights. Conversations.
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Other apps give you charts and numbers. RunLens gives you a coach
            you can actually talk to - one who knows your training inside out.
          </p>
        </div>

        {/* Featured AI Coach card */}
        <div className="mb-8">
          <div className="relative p-8 lg:p-12 bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl text-white overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-500 rounded-full opacity-20 blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-400 rounded-full opacity-20 blur-3xl translate-y-1/2 -translate-x-1/2" />

            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6 backdrop-blur-sm">
                  <ChatBubbleLeftRightIcon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                  Talk to Your AI Coach
                </h3>
                <p className="text-brand-100 text-lg leading-relaxed">
                  Have real conversations about your training. Ask why your pace
                  dropped, whether you're overtraining, or how to prepare for
                  race day. Get thoughtful answers and ask follow-up questions -
                  just like chatting with a real coach.
                </p>
              </div>

              {/* Activity Analysis Screenshot */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsLightboxOpen(true)}
                  className="relative bg-white rounded-xl shadow-2xl overflow-hidden cursor-zoom-in transition-transform hover:scale-[1.02]"
                >
                  <img
                    src="/assets/activity-analysis-screenshot.png"
                    alt="RunLens activity analysis view showing graphs, AI insights, and follow-up questions"
                    className="w-full h-auto"
                  />
                  {/* Zoom indicator overlay */}
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                      <MagnifyingGlassPlusIcon className="w-6 h-6 text-gray-700" />
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary feature cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {secondaryFeatures.map((feature, index) => (
            <div
              key={index}
              className="relative p-8 bg-gray-50 rounded-2xl hover:bg-brand-50 transition-colors group"
            >
              <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-brand-200 transition-colors">
                <feature.icon className="w-6 h-6 text-brand-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close lightbox"
          >
            <XMarkIcon className="w-6 h-6 text-white" />
          </button>

          {/* Full-size image */}
          <img
            src="/assets/activity-analysis-screenshot.png"
            alt="RunLens activity analysis view showing graphs, AI insights, and follow-up questions"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Hint text */}
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Click anywhere to close
          </p>
        </div>
      )}
    </section>
  );
}
